import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "react-hot-toast";
import Button from "../Button/Button";
import { RiErrorWarningFill } from "react-icons/ri";
import { GrFormClose } from "react-icons/gr";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import { PDFDocument } from "pdf-lib";
import UploadFileIcon from "../icons/uploadFile";

const UploadBox = ({ file, setFile, size }) => {
  const { t, i18n } = useTranslation();
  const [fileLength, setFileLength] = useState(0);
  const [upload, setUpload] = useState(false);
  const [progress, setProgress] = useState(0);
  const navigate = useNavigate();

  const maxFileSize = 200 * 1024 * 1024; // 200MB
  const minFileSize = 0;
  const maxFileLength = 500; // Maximum pages allowed

  const validatePDF = async (file) => {

    try {
      // 1. File type validation
      if (!file.type.includes("pdf")) {
        return { isValid: false, error: "Please upload a valid PDF file" };
      }

      // 2. Size validation
      if (file.size > maxFileSize) {
        const maxSizeMB = maxFileSize / (1024 * 1024);
        return {
          isValid: false,
          error: `File size cannot be more than ${maxSizeMB} MB`,
        };
      }

      if (file.size < minFileSize) {
        return { isValid: false, error: "File size cannot be empty" };
      }

      // 3. PDF specific validations
      try {
        const arrayBuffer = await file.arrayBuffer();

        // Load PDF with ignoreEncryption option
        const pdfDoc = await PDFDocument.load(arrayBuffer, {
          ignoreEncryption: true,
          updateMetadata: false,
        });

        // Check if PDF is encrypted
        if (pdfDoc.isEncrypted) {
          return {
            isValid: false,
            error: "Password protected PDFs are not supported",
          };
        }

        // Check page count
        const pageCount = pdfDoc.getPageCount();
        if (pageCount === 0) {
          return { isValid: false, error: "The PDF file is empty" };
        }

        if (pageCount > maxFileLength) {
          return {
            isValid: false,
            error: `${file.name} contains more than ${maxFileLength} pages and cannot be uploaded.`,
          };
        }

        setFileLength(pageCount);
        return { isValid: true };
      } catch (error) {
        console.error("PDF validation error:", error);

        // Handle specific PDF errors
        if (error instanceof Error) {
          if (error.message.includes("encrypted")) {
            return {
              isValid: false,
              error: "Password protected PDFs are not supported",
            };
          } else if (error.message.includes("Failed to parse PDF document")) {
            return {
              isValid: false,
              error: "The PDF file is corrupted or invalid",
            };
          }
        }

        return {
          isValid: false,
          error: "The selected PDF file is corrupted. Please select a valid PDF file.",
        };
      }
    } catch (error) {
      console.error("Validation error:", error);
      return { isValid: false, error: "Error validating PDF file" };
    }
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (!+bytes) return "0 Bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  };

  const handleFileUpload = async (file) => {
    try {
      setUpload(true);
      setProgress(100);

      // Clear existing cookies
      Cookies.remove("file_Id", {
        domain: process.env.REACT_APP_PUBLIC_DOMAIN,
        path: "/",
      });

      Cookies.remove("file_url", {
        domain: process.env.REACT_APP_PUBLIC_DOMAIN,
        path: "/",
      });

      // Set new file cookie
      Cookies.set("file_name", file?.name, {
        domain: process.env.NEXT_PUBLIC_DOMAIN,
        path: "/",
      });

      setFile(file);

      // Redirect to editing page after upload
      navigate(`/${i18n.language}`);
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error(t("Error uploading file"));
      setUpload(false);
      setProgress(0);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    multiple: false,
    accept: {
      "image/pdf": [".pdf"],
    },
    onDrop: async (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;

      const validationResult = await validatePDF(file);
      if (!validationResult.isValid) {
        toast.error(t(validationResult.error));
        return;
      }

      await handleFileUpload(file);
    },
  });

  return (
    <div className="w-full">
      {!file && (
        <div
          {...getRootProps({ className: "dropzone" })}
          className="flex flex-wrap w-full justify-center items-center md:p-4 rounded-2xl bg-primary-400 drop-shadow-7xl"
        >
          <div className="cursor-pointer flex justify-center items-center border border-primary border-dashed px-6 py-10 rounded-xl w-full">
            <input
              id="pdfFiles"
              accept=".pdf"
              type="file"
              className="w-full absolute"
              value={[]}
              {...getInputProps()}
            />
            <div className="flex flex-col justify-center items-center w-full text-center">
              <UploadFileIcon
                className={`mb-4 ${
                  size === "sm" ? "w-24 h-32" : "w-24 h-24 md:w-40 md:h-40"
                }`}
              />

              <h4 className="text-secondary-1000 text-2xl !font-medium mb-2">
                {t("Drag and Drop")}
              </h4>
              <p className="mb-1 text-secondary-650 text-base text-center">
                {" "}
                <span className="border-b border-secondary-650">
                  {t("Click to upload PDF files")}
                </span>{" "}
                {t("or drag and drop")}
              </p>
              <p className="mb-1 text-secondary-650 text-base text-center">
                {" "}
                <span className="border-b border-secondary-650">
                  {t("File should be less than 200 MB")}
                </span>{" "}
                {t("and allowed page size is 500")}
              </p>
            </div>
          </div>
        </div>
      )}

      {file && (
        <div className="flex flex-wrap w-full justify-center items-center md:p-4 rounded-2xl bg-primary-400 drop-shadow-7xl mb-6">
          <div className="flex justify-center items-center border border-primary border-dashed px-6 py-10 rounded-xl w-full">
            <GrFormClose
              onClick={() => {
                setFile(null);
                setUpload(false);
                setProgress(0);
              }}
              className={`${i18n.language === "ar" ? "left-5" : "right-5"}
                absolute top-5 text-3xl text-[#2E2E2E] cursor-pointer`}
            />

            <div className="flex flex-col justify-center items-center w-full text-center">
              <UploadFileIcon
                className={`mb-4 ${
                  size === "sm" ? "w-24 h-32" : "w-24 h-24 md:w-40 md:h-40"
                }`}
              />
              <p className="text-secondary-1000 text-2xl !font-semibold mb-2">
                {file?.name}
              </p>
              <p className="mb-1 text-secondary-650 text-base text-center">
                {formatBytes(file.size)}
              </p>
              {file && (
                <div className="flex justify-center items-center mt-3">
                  <Link to={`/${i18n.language}`}>
                    <Button className="!w-max mt-3 py-3.5">
                      {t("Start Editing")}
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {fileLength > maxFileLength && (
        <div className="flex items-center justify-center gap-2 w-full">
          <RiErrorWarningFill className="h-max w-10" color="#B91C1C" />
          <p className="text-red-700">
            {t("Max number of pages in current plan is 500 pages.")}
          </p>
        </div>
      )}
    </div>
  );
};

export default UploadBox;
